/**
 * @file AnalogClock.js
 * @version 1.2.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2025-10-02
 * @modified 2026-03-24
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import './AnalogClock.css';

function AnalogClock() {
  const [time, setTime] = useState(new Date());
  const [faceSize, setFaceSize] = useState(0);
  const containerRef = useRef(null);
  const digitalRef = useRef(null);

  useEffect(() => {
    const timer = setInterval(() => {
      setTime(new Date());
    }, 100);
    return () => clearInterval(timer);
  }, []);

  const recalcSize = useCallback(() => {
    const container = containerRef.current;
    const digital = digitalRef.current;
    if (!container || !digital) return;
    const w = container.clientWidth;
    const h = container.clientHeight - digital.offsetHeight - 4; // 4 = gap
    const size = Math.max(Math.floor(Math.min(w, h) * 0.9), 0);
    setFaceSize(size);
  }, []);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const ro = new ResizeObserver(recalcSize);
    ro.observe(container);
    recalcSize();
    return () => ro.disconnect();
  }, [recalcSize]);

  const hours = time.getHours() % 12;
  const minutes = time.getMinutes();
  const seconds = time.getSeconds();
  const milliseconds = time.getMilliseconds();

  const secondsWithMs = seconds + (milliseconds / 1000);
  const hourDegrees = (hours * 30) + (minutes * 0.5) + (secondsWithMs * 0.5 / 60);
  const minuteDegrees = (minutes * 6) + (secondsWithMs * 0.1);
  const secondDegrees = secondsWithMs * 6;

  return (
    <div className="analog-clock" ref={containerRef}>
      <div
        className="clock-face"
        style={faceSize ? { width: faceSize, height: faceSize } : { visibility: 'hidden' }}
      >
        {/* Hour markers */}
        <div className="hour-marker marker-12"></div>
        <div className="hour-marker marker-3"></div>
        <div className="hour-marker marker-6"></div>
        <div className="hour-marker marker-9"></div>

        {/* Clock hands */}
        <div
          className="clock-hand hour-hand"
          style={{ transform: `rotate(${hourDegrees}deg)` }}
        ></div>
        <div
          className="clock-hand minute-hand"
          style={{ transform: `rotate(${minuteDegrees}deg)` }}
        ></div>
        <div
          className="clock-hand second-hand"
          style={{ transform: `rotate(${secondDegrees}deg)` }}
        ></div>

        {/* Center dot */}
        <div className="clock-center"></div>
      </div>

      <div className="digital-time" ref={digitalRef}>
        {time.toLocaleTimeString('en-US', {
          hour: 'numeric',
          minute: '2-digit',
          second: '2-digit',
          hour12: false
        })}
      </div>
    </div>
  );
}

export default AnalogClock;
